/**
 * Leitor mínimo de ZIP, só o suficiente para achar um arquivo dentro do
 * `exportar.zip` do app Saúde e devolvê-lo como stream.
 *
 * Por que na mão: o navegador tem `DecompressionStream`, que sabe inflar
 * deflate, mas não sabe nada sobre o CONTÊINER zip — o índice de arquivos, os
 * offsets, os cabeçalhos. Uma biblioteca resolveria, mas as populares carregam
 * o arquivo inteiro na memória, e o export da Laís tem centenas de MB. Aqui a
 * gente lê só a cauda (o índice) e depois fatia o intervalo de bytes do arquivo
 * que interessa, sem nunca materializar o resto.
 *
 * Fora do escopo, de propósito: criptografia, spanning em vários volumes e
 * métodos de compressão que não sejam "guardado" ou "deflate". O app Saúde não
 * usa nenhum deles.
 */

const ASSINATURA_EOCD = 0x0605_4b50;
const ASSINATURA_EOCD64 = 0x0606_4b50;
const ASSINATURA_LOCALIZADOR64 = 0x0706_4b50;
const ASSINATURA_CENTRAL = 0x0201_4b50;
const ASSINATURA_LOCAL = 0x0403_4b50;

/** Sentinela do zip para "este número não coube em 32 bits, veja o ZIP64". */
const ESTOUROU_32 = 0xffff_ffff;

export interface EntradaZip {
  nome: string;
  /** 0 = guardado sem compressão, 8 = deflate. */
  metodo: number;
  tamanhoComprimido: number;
  tamanhoOriginal: number;
  offsetCabecalhoLocal: number;
}

export class ZipInvalido extends Error {
  constructor(mensagem: string) {
    super(mensagem);
    this.name = "ZipInvalido";
  }
}

async function ler(blob: Blob, inicio: number, fim: number): Promise<DataView> {
  const buf = await blob.slice(inicio, fim).arrayBuffer();
  return new DataView(buf);
}

/** Lê inteiro de 64 bits como Number. Acima de 2^53 lançamos em vez de mentir. */
function u64(v: DataView, off: number): number {
  const n = v.getBigUint64(off, true);
  if (n > BigInt(Number.MAX_SAFE_INTEGER)) throw new ZipInvalido("Arquivo grande demais.");
  return Number(n);
}

/** Lista os arquivos do zip lendo só o índice, no fim do arquivo. */
export async function lerIndice(arquivo: Blob): Promise<EntradaZip[]> {
  // O EOCD fica no fim, mas pode ter até 64 KiB de comentário depois dele.
  const janela = Math.min(arquivo.size, 65_535 + 22);
  const cauda = await ler(arquivo, arquivo.size - janela, arquivo.size);

  let posEocd = -1;
  for (let i = cauda.byteLength - 22; i >= 0; i--) {
    if (cauda.getUint32(i, true) === ASSINATURA_EOCD) {
      posEocd = i;
      break;
    }
  }
  if (posEocd === -1) throw new ZipInvalido("Não parece um arquivo .zip.");

  let quantidade = cauda.getUint16(posEocd + 10, true);
  let inicioCentral = cauda.getUint32(posEocd + 16, true);
  let tamanhoCentral = cauda.getUint32(posEocd + 12, true);

  // ZIP64: se qualquer campo estourou 32 bits, o valor real está em outro
  // registro. Um export de muitos anos chega perto disso; ignorar daria um
  // offset absurdo e um erro incompreensível na tela dela.
  if (
    quantidade === 0xffff ||
    inicioCentral === ESTOUROU_32 ||
    tamanhoCentral === ESTOUROU_32
  ) {
    const posLoc = posEocd - 20;
    if (posLoc < 0 || cauda.getUint32(posLoc, true) !== ASSINATURA_LOCALIZADOR64) {
      throw new ZipInvalido("Zip grande demais e sem índice ZIP64.");
    }
    const offEocd64 = u64(cauda, posLoc + 8);
    const v = await ler(arquivo, offEocd64, offEocd64 + 56);
    if (v.getUint32(0, true) !== ASSINATURA_EOCD64) throw new ZipInvalido("Índice ZIP64 corrompido.");
    quantidade = u64(v, 32);
    tamanhoCentral = u64(v, 40);
    inicioCentral = u64(v, 48);
  }

  const central = await ler(arquivo, inicioCentral, inicioCentral + tamanhoCentral);
  const decodificador = new TextDecoder("utf-8");
  const entradas: EntradaZip[] = [];
  let p = 0;

  for (let i = 0; i < quantidade && p + 46 <= central.byteLength; i++) {
    if (central.getUint32(p, true) !== ASSINATURA_CENTRAL) break;

    const metodo = central.getUint16(p + 10, true);
    let tamanhoComprimido = central.getUint32(p + 20, true);
    let tamanhoOriginal = central.getUint32(p + 24, true);
    const nomeLen = central.getUint16(p + 28, true);
    const extraLen = central.getUint16(p + 30, true);
    const comentarioLen = central.getUint16(p + 32, true);
    let offsetLocal = central.getUint32(p + 42, true);

    const nome = decodificador.decode(
      new Uint8Array(central.buffer, central.byteOffset + p + 46, nomeLen),
    );

    // O campo extra 0x0001 traz os valores de 64 bits, e SÓ os que estouraram,
    // nesta ordem fixa. Ler fora de ordem embaralha tamanho com offset.
    if (
      tamanhoOriginal === ESTOUROU_32 ||
      tamanhoComprimido === ESTOUROU_32 ||
      offsetLocal === ESTOUROU_32
    ) {
      let e = p + 46 + nomeLen;
      const fimExtra = e + extraLen;
      while (e + 4 <= fimExtra) {
        const id = central.getUint16(e, true);
        const tam = central.getUint16(e + 2, true);
        if (id === 0x0001) {
          let q = e + 4;
          if (tamanhoOriginal === ESTOUROU_32) { tamanhoOriginal = u64(central, q); q += 8; }
          if (tamanhoComprimido === ESTOUROU_32) { tamanhoComprimido = u64(central, q); q += 8; }
          if (offsetLocal === ESTOUROU_32) { offsetLocal = u64(central, q); q += 8; }
          break;
        }
        e += 4 + tam;
      }
    }

    entradas.push({
      nome,
      metodo,
      tamanhoComprimido,
      tamanhoOriginal,
      offsetCabecalhoLocal: offsetLocal,
    });
    p += 46 + nomeLen + extraLen + comentarioLen;
  }

  return entradas;
}

/**
 * Devolve o conteúdo de uma entrada como stream de bytes já descomprimido.
 *
 * Os tamanhos vêm do índice central, nunca do cabeçalho local: quando o zip é
 * escrito em stream, o cabeçalho local traz zeros e o tamanho real só aparece
 * DEPOIS dos dados. Confiar nele daria um arquivo vazio.
 */
export async function abrirEntrada(
  arquivo: Blob,
  entrada: EntradaZip,
): Promise<ReadableStream<Uint8Array>> {
  const cabecalho = await ler(
    arquivo,
    entrada.offsetCabecalhoLocal,
    entrada.offsetCabecalhoLocal + 30,
  );
  if (cabecalho.getUint32(0, true) !== ASSINATURA_LOCAL) {
    throw new ZipInvalido(`Cabeçalho local inválido em "${entrada.nome}".`);
  }
  const inicio =
    entrada.offsetCabecalhoLocal +
    30 +
    cabecalho.getUint16(26, true) +
    cabecalho.getUint16(28, true);

  const bruto = arquivo.slice(inicio, inicio + entrada.tamanhoComprimido).stream();
  if (entrada.metodo === 0) return bruto as ReadableStream<Uint8Array>;
  if (entrada.metodo !== 8) {
    throw new ZipInvalido(`Compressão não suportada (método ${entrada.metodo}).`);
  }
  // "deflate-raw" porque dentro do zip não há cabeçalho zlib.
  //
  // O cast existe porque a lib.dom declara `DecompressionStream.writable` como
  // `WritableStream<BufferSource>`, e `WritableStream` é invariante no
  // TypeScript — um sorvedouro que aceita MAIS coisa não é atribuível a um que
  // aceita menos, mesmo sendo seguro na prática. É desencontro de declaração,
  // não de comportamento.
  return (bruto as ReadableStream<Uint8Array>).pipeThrough(
    new DecompressionStream("deflate-raw") as unknown as ReadableWritablePair<
      Uint8Array,
      Uint8Array
    >,
  );
}
